const positiveId = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
};

const finiteNumber = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const enabledFlag = (value) => (
    value === true
    || value === 1
    || ['1', 'true', 'yes'].includes(String(value ?? '').trim().toLowerCase())
);

const dateParts = (value) => {
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (year < 1970 || year > 2038) return null;
    if (match[4] && (Number(match[4]) > 23 || Number(match[5]) > 59 || Number(match[6]) > 59)) return null;
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
    return { normalized: `${match[1]}-${match[2]}-${match[3]}`, year, month, day };
};

const normalizedDate = (value) => dateParts(value)?.normalized || '';

const rawBillingEntryQuantity = (entry = {}) => (
    entry.final_quantity !== null && entry.final_quantity !== undefined && entry.final_quantity !== ''
        ? entry.final_quantity
        : (entry.raw_quantity ?? entry.quantity ?? entry.hours)
);

const ISSUE_LABELS = Object.freeze({
    missing_date: 'Add a date',
    invalid_date: 'Enter a valid date',
    missing_description: 'Add a description',
    missing_team_member: 'Assign a team member',
    missing_rate: 'Add a rate',
    invalid_rate: 'Rate must be greater than $0',
    missing_quantity: 'Add hours',
    invalid_quantity: 'Hours must be greater than 0',
    invalid_total: 'Total must be greater than $0',
});

export const createBillingEntryListFilters = () => ({
    type: '',
    teamMember: '',
    invoiceState: '',
    attention: '',
    dateFrom: '',
    dateTo: '',
});

export const getBillingEntryKind = (entry = {}) => {
    const kind = String(entry.entry_kind || entry.item_type || 'time').trim().toLowerCase();
    if (kind === 'flatfee' || kind === 'flat') return 'flat_fee';
    return kind || 'time';
};

export const getBillingEntryQuantity = (entry = {}) => {
    return finiteNumber(rawBillingEntryQuantity(entry));
};

export const getBillingEntryAuthoritativeTotal = (entry = {}) => {
    const finalTotal = finiteNumber(entry.final_total);
    if (finalTotal !== null) return Math.round((finalTotal + Number.EPSILON) * 100) / 100;

    const rate = finiteNumber(entry.rate);
    const quantity = getBillingEntryQuantity(entry);
    if (rate === null || quantity === null) return null;
    return Math.round(((rate * quantity) + Number.EPSILON) * 100) / 100;
};

export const getBillingEntryEffectiveUserId = (entry = {}, rateEntries = []) => {
    const candidates = [
        entry.user_id,
        entry.billing_rate_user_id,
        entry.effective_user_id,
        entry.rate_user_id,
        entry.timekeeper_user_id,
        entry.user?.id,
        entry.billing_rate?.user_id,
    ];
    for (const candidate of candidates) {
        const id = positiveId(candidate);
        if (id) return id;
    }

    const billingRateId = positiveId(entry.billing_rate_id);
    if (!billingRateId) return null;
    const rate = (Array.isArray(rateEntries) ? rateEntries : []).find(
        candidate => positiveId(candidate?.id) === billingRateId
    );
    return positiveId(rate?.user_id ?? rate?.effective_user_id);
};

export const getBillingEntryEffectiveUserName = (entry = {}, users = [], rateEntries = []) => {
    const apiName = String(entry.user_name || entry.team_member_name || '').trim();
    if (apiName) return apiName;

    const userId = getBillingEntryEffectiveUserId(entry, rateEntries);
    if (!userId) return '';
    const user = (Array.isArray(users) ? users : []).find(candidate => positiveId(candidate?.id) === userId);
    return String(user?.name || user?.full_name || user?.user || '').trim();
};

/**
 * Compatibility classifier for a frontend deployed before the attention API.
 * Backend-provided attention state remains authoritative whenever it exists.
 */
export const classifyBillingEntryAttention = (entry = {}, rateEntries = []) => {
    if (
        getBillingEntryKind(entry) !== 'time'
        || Number(entry.deleted || 0) !== 0
        || Number(entry.status || 0) === 2
    ) {
        return {
            needs_attention: false,
            issues: [],
            issue_codes: [],
            issue_fingerprint: '',
            ignored: false,
            ignored_at: null,
            ignored_by_user_id: null,
            source: 'fallback',
        };
    }

    const issues = [];
    const addIssue = (code) => issues.push({ code, label: ISSUE_LABELS[code] || code });
    const rawDate = entry.date ?? entry.entry_date;
    if (rawDate === null || rawDate === undefined || String(rawDate).trim() === '') addIssue('missing_date');
    else if (!dateParts(rawDate)) addIssue('invalid_date');
    if (!String(entry.description ?? '').trim()) addIssue('missing_description');
    const resolvedUserMissing = Object.prototype.hasOwnProperty.call(entry, 'effective_user_record_id')
        ? !positiveId(entry.effective_user_record_id)
        : (Object.prototype.hasOwnProperty.call(entry, 'user_name')
            && !String(entry.user_name ?? '').trim());
    if (!getBillingEntryEffectiveUserId(entry, rateEntries) || resolvedUserMissing) addIssue('missing_team_member');

    const rawRate = entry.rate;
    const rate = finiteNumber(entry.rate);
    const validRate = rate !== null && rate > 0;
    if (rawRate === null || rawRate === undefined || rawRate === '') addIssue('missing_rate');
    else if (!validRate) addIssue('invalid_rate');

    const rawQuantity = rawBillingEntryQuantity(entry);
    const quantity = finiteNumber(rawQuantity);
    const validQuantity = quantity !== null && quantity > 0;
    if (rawQuantity === null || rawQuantity === undefined || rawQuantity === '') addIssue('missing_quantity');
    else if (!validQuantity) addIssue('invalid_quantity');
    if (validRate && validQuantity) {
        const total = getBillingEntryAuthoritativeTotal(entry);
        if (total === null || total <= 0) addIssue('invalid_total');
    }

    return {
        needs_attention: issues.length > 0,
        issues,
        issue_codes: issues.map(issue => issue.code),
        issue_fingerprint: '',
        ignored: false,
        ignored_at: null,
        ignored_by_user_id: null,
        source: 'fallback',
    };
};

export const getBillingEntryAttention = (entry = {}, rateEntries = []) => {
    const backend = entry?.attention;
    if (!backend || typeof backend !== 'object' || Array.isArray(backend)) {
        return classifyBillingEntryAttention(entry, rateEntries);
    }

    const codes = Array.isArray(backend.issue_codes)
        ? backend.issue_codes.map(String).filter(Boolean)
        : [];
    let issues = Array.isArray(backend.issues)
        ? backend.issues
            .filter(issue => issue && typeof issue === 'object')
            .map(issue => {
                const code = String(issue.code || '').trim();
                return {
                    code,
                    label: String(issue.label || ISSUE_LABELS[code] || code || 'Needs attention'),
                };
            })
        : [];
    if (issues.length === 0 && codes.length > 0) {
        issues = codes.map(code => ({ code, label: ISSUE_LABELS[code] || code }));
    }

    const fingerprint = String(backend.issue_fingerprint || '').trim();
    return {
        needs_attention: enabledFlag(backend.needs_attention),
        issues,
        issue_codes: codes.length > 0 ? codes : issues.map(issue => issue.code).filter(Boolean),
        issue_fingerprint: fingerprint,
        // Ignore state is trusted only when it was returned with the backend's
        // issue fingerprint. The local classifier never invents persistence.
        ignored: fingerprint !== '' && enabledFlag(backend.ignored),
        ignored_at: backend.ignored_at || null,
        ignored_by_user_id: positiveId(backend.ignored_by_user_id),
        source: 'backend',
    };
};

export const billingEntryHasInvoiceLink = (entry = {}) => (
    positiveId(entry.time_invoice_id) !== null || positiveId(entry.invoice_id) !== null
);

export const billingEntryMatchesListFilters = (
    entry = {},
    { query = '', filters = createBillingEntryListFilters(), users = [], rateEntries = [] } = {}
) => {
    const next = { ...createBillingEntryListFilters(), ...(filters || {}) };
    const kind = getBillingEntryKind(entry);
    const userId = getBillingEntryEffectiveUserId(entry, rateEntries);
    const userName = getBillingEntryEffectiveUserName(entry, users, rateEntries);
    const attention = getBillingEntryAttention(entry, rateEntries);
    const missingTeamMember = attention.issue_codes.includes('missing_team_member')
        || userId === null
        || userName === '';
    const invoiced = billingEntryHasInvoiceLink(entry);
    const entryDate = normalizedDate(entry.date || entry.entry_date);
    const quantity = getBillingEntryQuantity(entry);
    const total = getBillingEntryAuthoritativeTotal(entry);
    const rate = finiteNumber(entry.rate);
    const issueCodes = new Set(attention.issue_codes);
    const hasMissingVisibleValue = issueCodes.has('missing_date')
        || issueCodes.has('missing_description')
        || issueCodes.has('missing_team_member')
        || issueCodes.has('missing_rate')
        || issueCodes.has('missing_quantity')
        || rate === null
        || quantity === null
        || total === null
        || userName === '';
    const currencySearchValues = (value) => value === null ? [] : [
        `$${value.toFixed(2)}`,
        `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    ];

    if (next.type && kind !== next.type) return false;
    if (next.teamMember === 'unassigned' && !missingTeamMember) return false;
    if (next.teamMember && next.teamMember !== 'unassigned'
        && (missingTeamMember || String(userId || '') !== String(next.teamMember))) return false;
    if (next.invoiceState === 'invoiced' && !invoiced) return false;
    if (next.invoiceState === 'unbilled' && invoiced) return false;
    if (next.attention === 'needs_attention' && (!attention.needs_attention || attention.ignored)) return false;
    if (next.attention === 'ignored' && (!attention.needs_attention || !attention.ignored)) return false;
    if (next.attention === 'complete' && attention.needs_attention) return false;
    if (next.dateFrom && (!entryDate || entryDate < next.dateFrom)) return false;
    if (next.dateTo && (!entryDate || entryDate > next.dateTo)) return false;

    const normalizedQuery = String(query || '').trim().toLowerCase();
    if (!normalizedQuery) return true;
    return [
        entry.id,
        entry.date,
        entry.entry_date,
        entry.description,
        userName || 'Unassigned',
        userId,
        entry.status || 'Saved',
        entry.time_invoice_id,
        entry.invoice_id,
        kind.replace(/_/g, ' '),
        rate,
        rate === null ? 'Missing rate' : `$${rate.toFixed(2)}`,
        ...currencySearchValues(rate),
        quantity,
        quantity === null ? 'Missing hours' : `${quantity.toFixed(2)} hours`,
        total,
        total === null ? 'Missing total' : `$${total.toFixed(2)}`,
        ...currencySearchValues(total),
        issueCodes.has('missing_date') ? 'Missing date' : '',
        issueCodes.has('invalid_date') ? 'Invalid date' : '',
        issueCodes.has('missing_description') ? 'Missing description' : '',
        hasMissingVisibleValue ? 'Missing' : '',
        attention.needs_attention ? 'Needs attention' : 'Complete',
        attention.ignored ? 'Warning ignored' : '',
        ...attention.issues.map(issue => issue.label),
    ].some(value => String(value ?? '').toLowerCase().includes(normalizedQuery));
};

const entryChronology = (entry = {}) => {
    const date = normalizedDate(entry.date || entry.entry_date);
    return date ? Number(date.replaceAll('-', '')) : 0;
};

export const sortBillingEntriesForList = (entries = [], rateEntries = []) => (
    [...(Array.isArray(entries) ? entries : [])].sort((left, right) => {
        const leftAttention = getBillingEntryAttention(left, rateEntries);
        const rightAttention = getBillingEntryAttention(right, rateEntries);
        const leftPinned = getBillingEntryKind(left) === 'time'
            && leftAttention.needs_attention
            && !leftAttention.ignored;
        const rightPinned = getBillingEntryKind(right) === 'time'
            && rightAttention.needs_attention
            && !rightAttention.ignored;
        if (leftPinned !== rightPinned) return leftPinned ? -1 : 1;

        const dateDifference = entryChronology(right) - entryChronology(left);
        if (dateDifference !== 0) return dateDifference;
        return (positiveId(right?.id) || 0) - (positiveId(left?.id) || 0);
    })
);
